import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateField } from "../../../reduxSlice/profileSlice";

export const validateStep2 = (data) => {
    const errors = {};
    const alphabetRegex = /^[A-Za-z\s]+$/;

    if (!data.maritalStatus) errors.maritalStatus = "Marital status is required";

    if (!data.birthPlace?.trim()) {
        errors.birthPlace = "Birth place is required";
    } else if (!alphabetRegex.test(data.birthPlace)) {
        errors.birthPlace = "Birth place must contain alphabets only";
    }

    if (!data.heightFeet) errors.heightFeet = "Height is required";

    if (!data.weight || isNaN(data.weight)) {
        errors.weight = "Enter a valid weight";
    } else {
        const weightNum = Number(data.weight);
        if (weightNum < 40 || weightNum > 200) {
            errors.weight = "Weight must be between 40 and 200 kg";
        }
    }

    if (!data.complexion) errors.complexion = "Complexion is required";
    if (!data.bodyBuild) errors.bodyBuild = "Body build is required";
    if (!data.bloodGroup) errors.bloodGroup = "Blood group is required";

    if(data.differentlyAbled) {
        const descriptionText = data.disabilityDescription?.trim() || "";
        const words = descriptionText ? descriptionText.split(/\s+/) : [];

        if (!descriptionText) {
            errors.disabilityDescription = "Please describe the disability in short";
        } else if (words.length < 4) {
            errors.disabilityDescription = `Description must contain at least 4 words (Current word count: ${words.length})`;
        } else if (words.length > 100) {
            errors.disabilityDescription = `Description cannot exceed 100 words (Current word count: ${words.length})`;
        }
    }
    return errors;
};

const Step2PersonalDetails = ({ masters = {} }) => {
    const dispatch = useDispatch();
    const formData = useSelector((state) => state.profile);
    const errors = useSelector((state) => state.profile.errors) || {};

    const maritalOptions = masters.MaritalStatuses && masters.MaritalStatuses.length > 0
        ? masters.MaritalStatuses
        : [{ name: "Never Married", value: "single" }, { name: "Divorced", value: "divorced" }, { name: "Widowed", value: "widowed" }];

    const feetOptions = masters.feet && masters.feet.length > 0
        ? masters.feet.map(ft => ({ name: `${ft} ft`, value: String(ft) }))
        : [3, 4, 5, 6, 7, 8].map(ft => ({ name: `${ft} ft`, value: String(ft) }));

    const inchesOptions = masters.inches && masters.inches.length > 0
        ? masters.inches.map(inch => ({ name: `${inch} in`, value: String(inch) }))
        : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(inch => ({ name: `${inch} in`, value: String(inch) }));

    const parseNormalizedFeet = () => {
        if (formData.heightFeet !== undefined && formData.heightFeet !== null && formData.heightFeet !== "") {
            const parsed = parseInt(formData.heightFeet, 10);
            return isNaN(parsed) ? "" : String(parsed);
        }
        return "";
    };

    const parseNormalizedInches = () => {
        if (formData.heightInches !== undefined && formData.heightInches !== null && formData.heightInches !== "") {
            const parsed = parseInt(formData.heightInches, 10);
            return isNaN(parsed) ? "0" : String(parsed);
        }
        return "0";
    };

    const currentWordCount = formData.disabilityDescription?.trim() ? formData.disabilityDescription.trim().split(/\s+/).length : 0;

    const handleInputChange = (field, value) => {
        let finalValue = value;

        if (field === "birthPlace") {
            finalValue = finalValue.replace(/[^A-Za-z\s]/g, "");
            if (finalValue.length > 0) {
                finalValue = finalValue.replace(/\b\w/g, (char) => char.toUpperCase());
            }
        }

        dispatch(updateField({ field, value: finalValue }));

        if (field === "differentlyAbled" && finalValue === false) {
            dispatch(updateField({ field: "disabilityDescription", value: "" }));
        }
        const simulatedData = { ...formData, [field]: finalValue };
        const generatedErrors = validateStep2(simulatedData);

        dispatch(updateField({
            field: "errors",
            value: {
                ...errors,
                [field]: generatedErrors[field] || "",
                ...(field === "differentlyAbled" && finalValue === false ? { disabilityDescription: "" } : {})
            }
        }));
    };

    return (
        <div className="form-animate">
            <div className="row">
                {/* Marital Status */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Marital Status<span className="m_feild">*</span></h5>
                        <select
                            className={`form-select input_style ${errors.maritalStatus ? "is-invalid" : ""}`}
                            value={formData.maritalStatus || ""}
                            onChange={(e) => handleInputChange("maritalStatus", e.target.value)}
                        >
                            <option value="">Select Status</option>
                            {maritalOptions.map((opt, i) => (
                                <option key={i} value={opt.value || opt.name.toLowerCase()}>{opt.name}</option>
                            ))}
                        </select>
                        {errors.maritalStatus && <div className="text-danger small mt-1">{errors.maritalStatus}</div>}
                    </div>
                </div>

                {/* Birth Place Field */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Birth Place<span className="m_feild">*</span></h5>
                        <input
                            className={`form-control input_style ${errors.birthPlace ? "is-invalid" : ""}`}
                            placeholder="Enter Birth Place"
                            value={formData.birthPlace || ""}
                            onChange={(e) => handleInputChange("birthPlace", e.target.value)}
                        />
                        {errors.birthPlace && <div className="text-danger small mt-1">{errors.birthPlace}</div>}
                    </div>
                </div>

                {/* Height Dropdowns */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Height<span className="m_feild">*</span></h5>
                        <div className="d-flex gap-2">
                            <select
                                className={`form-select input_style ${errors.heightFeet ? "is-invalid" : ""}`}
                                value={parseNormalizedFeet()}
                                onChange={(e) => handleInputChange("heightFeet", e.target.value)}
                            >
                                <option value="">Select Ft</option>
                                {feetOptions.map((opt, i) => (
                                    <option key={i} value={opt.value !== undefined ? opt.value : opt.name}>{opt.name}</option>
                                ))}
                            </select>

                            <select
                                className={`form-select input_style ${errors.heightInches ? "is-invalid" : ""}`}
                                value={parseNormalizedInches()}
                                onChange={(e) => handleInputChange("heightInches", e.target.value)}
                            >
                                {inchesOptions.map((opt, i) => (
                                    <option key={i} value={opt.value !== undefined ? opt.value : opt.name}>{opt.name}</option>
                                ))}
                            </select>
                        </div>
                        {errors.heightFeet && <div className="text-danger small mt-1">{errors.heightFeet}</div>}
                    </div>
                </div>

                {/* Weight */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Weight<span className="m_feild">*</span></h5>
                        <div className="position-relative">
                            <input
                                className={`form-control input_style ${errors.weight ? "is-invalid" : ""}`}
                                placeholder="Enter Weight"
                                value={parseFloat(formData.weight) || ""}
                                onChange={(e) => handleInputChange("weight", e.target.value)}
                            />
                            <div className="kg_txt">Kg</div>
                        </div>
                        {errors.weight && <div className="text-danger small mt-1">{errors.weight}</div>}
                    </div>
                </div>

                {/* Complexion */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Complexion<span className="m_feild">*</span></h5>
                        <select
                            className={`form-select input_style ${errors.complexion ? "is-invalid" : ""}`}
                            value={formData.complexion || ""}
                            onChange={(e) => handleInputChange("complexion", e.target.value)}
                        >
                            <option value="">Select Complexion</option>
                            {(masters.Complexion || []).map((opt, i) => (
                                <option key={i} value={opt.name.toLowerCase()}>{opt.name}</option>
                            ))}
                        </select>
                        {errors.complexion && <div className="text-danger small mt-1">{errors.complexion}</div>}
                    </div>
                </div>

                {/* Body Build */}
                <div className="col-md-6">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Body Build<span className="m_feild">*</span></h5>
                        <select
                            className={`form-select input_style ${errors.bodyBuild ? "is-invalid" : ""}`}
                            value={formData.bodyBuild || ""}
                            onChange={(e) => handleInputChange("bodyBuild", e.target.value)}
                        >
                            <option value="">Select Body Build</option>
                            {(masters.BodyBuild || []).map((opt, i) => (
                                <option key={i} value={opt.name.toLowerCase()}>{opt.name}</option>
                            ))}
                        </select>
                        {errors.bodyBuild && <div className="text-danger small mt-1">{errors.bodyBuild}</div>}
                    </div>
                </div>

                {/* Blood Group */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <h5 className="input_label">Blood Group<span className="m_feild">*</span></h5>
                        <select
                            className={`form-select input_style ${errors.bloodGroup ? "is-invalid" : ""}`}
                            value={formData.bloodGroup || ""}
                            onChange={(e) => handleInputChange("bloodGroup", e.target.value)}
                        >
                            <option value="">Select Blood Group</option>
                            {(masters.BloodGroup || []).map((opt, i) => (
                                <option key={i} value={opt.name}>{opt.name}</option>
                            ))}
                        </select>
                        {errors.bloodGroup && <div className="text-danger small mt-1">{errors.bloodGroup}</div>}
                    </div>
                </div>

                {/* Differently Abled Checkbox */}
                <div className="col-md-12">
                    <div className="inputcontainer mb-3">
                        <div className="form-check m-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="differentlyAbled"
                                checked={formData.differentlyAbled || false}
                                onChange={(e) => handleInputChange("differentlyAbled", e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="differentlyAbled">Person with Disability?</label>
                        </div>
                    </div>
                </div>

                {formData.differentlyAbled && (
                    <div className="col-md-12 animate-fade-in">
                        <div className="inputcontainer mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="input_label">Please describe the disability in short <span className="text-danger">*</span></h5>
                                <small className={`text-muted ${currentWordCount > 100 ? "text-danger" : ""}`} style={{ fontSize: "11px" }}>
                                    {currentWordCount} / 100 words
                                </small>
                            </div>
                            <textarea
                                className={`form-control input_style ${errors.disabilityDescription ? 'is-invalid' : ''}`}
                                id="disabilityDescription"
                                name="disabilityDescription"
                                placeholder="Enter details here..."
                                rows="3"
                                value={formData.disabilityDescription || ""}
                                onChange={(e) => handleInputChange("disabilityDescription", e.target.value)}
                            />
                            {errors.disabilityDescription && (
                                <div className="text-danger small mt-1 fw-bold">{errors.disabilityDescription}</div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Step2PersonalDetails;